import {
  FeedbackListActionType,
  FeedbackListAction,
  IFeedbackSearchLoadedActionPayload,
  IFeedbackUpdateListItemPayload,
  ISetSearchErroredActionPayload,
  IFeedbackTransitionsLoadedActionPayload,
  IFeedbackSearchSetSavedFiltersActionPayload
} from "../Actions/FeedbackListActions";
import { Feedback } from "../Models/Feedback";

export interface FeedbackListsState {
  lists: Record<string, FeedbackListState>;
}

export interface FeedbackListState {
  feedbacks: Feedback[];
  fullResultsCount: number;
  isLoaded: boolean;
  isLoading: boolean;
  isErrored: boolean;
  areTransitionsLoaded: boolean;
  areTransitionsLoading: boolean;
  errorMessage: string;
  errorCode?: number;
  savedFilters?: any[];
  isInitialFilterSaved?: boolean;
}

const initialFeedbackListsState: FeedbackListsState = {
  lists: {} as Record<string, FeedbackListState>
};

const updateObject = (oldObject: any, newValues: any) => ({ ...oldObject, ...newValues });

function updateListState(
  oldState: FeedbackListsState,
  listId: string,
  newValues: any
): FeedbackListsState {
  // clone the dictionary
  const newLists: Record<string, FeedbackListState> = updateObject(oldState.lists, {});

  let newList: FeedbackListState;
  if (newLists[listId] === undefined) {
    // create a new record
    newList = updateObject({ feedbacks: [], isLoaded: false }, newValues);
  } else {
    // clone the item we are going to change
    newList = updateObject(newLists[listId], newValues);
  }
  // update our cloned dictionary
  newLists[listId] = newList;
  // clone the full state and return
  return updateObject(oldState, { lists: newLists });
}

function updateFeedbackInList(existingList: Feedback[], updatedFeedback: Feedback): Feedback[] {
  return existingList.map((feedback, index) => {
    // check to see if this is the item we need to update
    if (feedback.code === updatedFeedback.code) {
      // this is the one to update, return an updated value
      return {
        ...feedback,
        ...updatedFeedback,
        updateCount: (feedback.updateCount == null ? 0 : feedback.updateCount) + 1
      };
    }
    // this is not the item we want to update, leave it as is
    return feedback;
  });
}

const feedbackListReducer = (
  feedbackListsState: FeedbackListsState = initialFeedbackListsState,
  action: FeedbackListAction
) => {
  switch (action.type) {
    case FeedbackListActionType.FEEDBACK_SEARCH_LOADED: {
      const payload = <IFeedbackSearchLoadedActionPayload>action.payload;

      const { loadedFeedbacks } = payload;

      // init the update count for each feedback (0 updates so far as this is the first time its loaded)
      for (let i = 0; i < loadedFeedbacks.length; i++) {
        loadedFeedbacks[i].updateCount = 0;
      }

      // let loadedFeedbacks = [];

      // if(feedbackListsState.lists[action.payload.listId] === undefined){
      //     loadedFeedbacks = payload.loadedFeedbacks;
      // }
      // else{
      //     loadedFeedbacks = feedbackListsState.lists[action.payload.listId].feedbacks.concat();
      // }

      return updateListState(feedbackListsState, action.payload.listId, {
        feedbacks: loadedFeedbacks,
        fullResultsCount: payload.fullResultsCount,
        isLoaded: true,
        isLoading: false,
        isErrored: false,
        areTransitionsLoaded: false,
        areTransitionsLoading: false
      });
    }
    case FeedbackListActionType.FEEDBACK_SEARCH_TRANSITIONS_LOADED: {
      if (feedbackListsState.lists[action.payload.listId] === undefined) {
        // nothing to update

        // eslint-disable-next-line no-console
        console.log(
          "Attempted to load search transitions, but that list does not exist in the State"
        );
        return feedbackListsState;
      }
      const payload = <IFeedbackTransitionsLoadedActionPayload>action.payload;
      const { loadedTransitions } = payload;

      // get the new feedbacks array
      const feedbacks = feedbackListsState.lists[action.payload.listId].feedbacks.map(item => {
        if (!(item.code in loadedTransitions)) {
          // this one does not have transitions to load, but still mark it as updated
          return {
            ...item,
            updateCount: (item.updateCount == null ? 0 : item.updateCount) + 1,
            possibleTransitions: []
          };
        }

        // update this item
        return {
          ...item,
          updateCount: (item.updateCount == null ? 0 : item.updateCount) + 1,
          possibleTransitions: loadedTransitions[item.code]
        };
      });

      return updateListState(feedbackListsState, action.payload.listId, {
        feedbacks,
        areTransitionsLoaded: true,
        areTransitionsLoading: false
      });
    }
    case FeedbackListActionType.FEEDBACK_SEARCH_UNLOAD: {
      return updateListState(feedbackListsState, action.payload.listId, {
        feedbacks: [],
        fullResultsCount: 0,
        isLoaded: false,
        isLoading: false,
        isErrored: false,
        areTransitionsLoaded: false,
        areTransitionsLoading: false
      });
    }
    case FeedbackListActionType.FEEDBACK_SEARCH_SET_LOADING: {
      return updateListState(feedbackListsState, action.payload.listId, {
        feedbacks: [],
        fullResultsCount: 0,
        isLoaded: false,
        isLoading: true,
        isErrored: false,
        areTransitionsLoaded: false,
        areTransitionsLoading: false
      });
    }
    case FeedbackListActionType.FEEDBACK_SEARCH_SET_TRANSITIONS_LOADING: {
      return updateListState(feedbackListsState, action.payload.listId, {
        areTransitionsLoaded: false,
        areTransitionsLoading: true
      });
    }
    case FeedbackListActionType.FEEDBACK_SEARCH_SET_ERRORED: {
      const payload = <ISetSearchErroredActionPayload>action.payload;
      return updateListState(feedbackListsState, payload.listId, {
        feedbacks: [],
        fullResultsCount: 0,
        isLoaded: false,
        isLoading: false,
        isErrored: true,
        errorMessage: payload.errorMessage,
        errorCode: payload.errorCode,
        areTransitionsLoaded: false,
        areTransitionsLoading: false
      });
    }
    case FeedbackListActionType.FEEDBACK_UPDATE_LIST_ITEM: {
      if (feedbackListsState.lists[action.payload.listId] === undefined) {
        // nothing to update

        // eslint-disable-next-line no-console
        console.log(
          "Attempted to update feedback in list, but that list does not exist in the State"
        );
        return feedbackListsState;
      }

      const payload = <IFeedbackUpdateListItemPayload>action.payload;

      // update the feedbacks array immutably
      const newFeedbacks = updateFeedbackInList(
        feedbackListsState.lists[action.payload.listId].feedbacks,
        payload.updatedFeedback
      );

      // now update the state
      return updateListState(feedbackListsState, payload.listId, {
        feedbacks: newFeedbacks,
        isLoaded: true,
        isLoading: false,
        isErrored: false
      });
    }
    case FeedbackListActionType.FEEDBACK_SEARCH_SET_SAVED_FILTERS: {
      const payload = <IFeedbackSearchSetSavedFiltersActionPayload>action.payload;

      const { filters } = payload;

      return updateListState(feedbackListsState, action.payload.listId, {
        savedFilters: filters,
        isInitialFilterSaved: true
      });
    }
    default:
      return feedbackListsState;
  }
};

export default feedbackListReducer;
